import { observer } from 'mobx-react'
import React, { ReactElement, useEffect, useMemo, useState } from 'react'
import Dropdown, { DropdownItem } from '../components/Dropdown'
import { cutString, formatCurrency, roundAssetAmount, shortAddress, successToast, useTestMode } from '../util/Util'
import Checkbox from '../components/Checkbox'
import PageHeader from '../components/PageHeader'
import { ButtonLeadingIcon, PrimaryButton } from '../components/Button'
import _ from 'lodash'
import { FIAT_ACCOUNT_FLAGS, FiatAccountType, FiatDestinationCurrency, RADOM_COLORS, VIRTUAL_ACCOUNT_CREATION_ENABLED, WINDOW_SIZE } from '../util/Constants'
import Table from '../components/Table'
import CreateVirtualAccountModal from '../components/CreateVirutalAccountModal'
import Radom, { BridgeVirtualAccountCapabilities, Deposit, DepositAccount, DepositAccountType, GeneralDepositMethod, PooledEurOnrampInstructions } from '../state/Radom'
import Spinner from '../components/Spinner'
import LoadingBar from '../components/LoadingBar'
import { Chevron } from '../icons/Chevron'
import { getMethod, getMethodLogoByTicker } from '../util/Managed'
import { NETWORK_NAMES, networkName } from '../util/AssetLabels'
import AccountDetailsDrawer, { DrawerField } from '../components/AccountDetailsDrawer'
import ConfirmButtonModal from '../components/ConfirmButtonModal'
import CopyButton from '../components/CopyButton'
import LinkExternal from '../components/LinkExternal'
import { ExternalLinkText } from './Events'
import TimeText from '../components/TimeText'
import { useNavigate } from 'react-router-dom'
import { endOfDay, format, subDays } from 'date-fns'
import Treasury from '../state/Treasury'
import User from '../state/User'
import styled from 'styled-components'
import { Container } from '../components/Animations'
import DashboardAreaChart from '../components/DashboardAreaChart'
import Gate from '../components/Gate'
import { usePermissions } from '../util/permissions'
import {
  DashboardPage,
  DetailGrid,
  EmptyState,
  InlinePill,
  PageActionRow,
  PageHeaderRow,
  SummaryLabel,
  SummaryMeta,
  SummaryStrip,
  SummaryStripItem,
  SummaryValue,
  Surface,
  SurfaceHeader,
  SurfaceTitle
} from '../components/DashboardPrimitives'

const FilterBar = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`

const AccountList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const AccountCard = styled.button`
  align-items: center;
  background: #fff;
  border: 1px solid #edf1f7;
  border-radius: 10px;
  color: #20242d;
  cursor: pointer;
  display: flex;
  gap: 14px;
  justify-content: space-between;
  padding: 16px;
  text-align: left;
  transition: 0.16s ease background-color, 0.16s ease border-color, 0.12s ease transform;
  width: 100%;

  &:hover {
    background: #f8fafc;
    border-color: #d9dee8;
  }

  &:active {
    transform: translateY(1px);
  }

  @media (max-width: ${WINDOW_SIZE.MOBILE_LARGE}) {
    align-items: flex-start;
    flex-direction: column;
  }
`

const AccountIdentity = styled.div`
  align-items: center;
  display: flex;
  gap: 10px;
  min-width: 0;
`

const AccountText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`

const AccountTitle = styled.span`
  color: #20242d;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.25;
`

const AccountMeta = styled.span`
  color: #7d8492;
  font-size: 12px;
  line-height: 1.3;
`

const AccountFlow = styled.div`
  align-items: center;
  color: #3b4250;
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
  justify-content: flex-end;
  min-width: 0;
`

const DepositTableShell = styled.div`
  background: #fff;
  border: 1px solid #edf1f7;
  border-radius: 14px;
  max-height: 420px;
  overflow: auto;
`

export interface FiatDepositSummary {
  in: {
    img: string | undefined
    amount: string | number
    ticker: string
  }
  out: {
    img: string | undefined
    amount: string | number
    ticker: string
  }
}

const PooledInstructionRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px solid ${RADOM_COLORS.GRAY_MED};
  font-size: 14px;

  &:last-child {
    border-bottom: none;
  }
`

/**
 * Bridge and Bastion name the chain a virtual account settles on in lowercase ("polygon", "base",
 * "avalanche_c_chain"); the registry ids are capitalised, so the first word with a capital reads through
 * networkName(). Accounts without a rail settle on Ethereum.
 */
const TEST_RAIL_VARIANTS = new Set(['SEPOLIA', 'DEVNET', 'TESTNET', 'AMOY', 'MUMBAI', 'GOERLI', 'NILE', 'SHASTA'])

const destinationRailNetwork = (rail: string | undefined): string => {
  const [chain = '', variant = ''] = (rail ?? '').split('_')
  if (!chain) return networkName('Ethereum')
  const id = chain.charAt(0).toUpperCase() + chain.slice(1).toLowerCase()
  if (!(id in NETWORK_NAMES)) return rail ?? ''
  if (TEST_RAIL_VARIANTS.has(variant.toUpperCase())) {
    return networkName(id === 'Solana' ? 'SolanaDevnet' : `${id}Testnet`)
  }
  return networkName(id)
}

const VirtualAccounts = observer((): ReactElement => {
  const [pooledEurInstructions, setPooledEurInstructions] = useState<PooledEurOnrampInstructions>()

  useEffect(() => {
    Radom.getPooledEurOnrampInstructions()
      .then(setPooledEurInstructions)
      .catch(() => setPooledEurInstructions(undefined))
  }, [])

  const batch = 100
  const navigate = useNavigate()
  const { has, isLoaded } = usePermissions()
  const canReadDeposits = has('ReadDeposit')

  const [testMode, setTestMode] = useTestMode()
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingDeposits, setIsLoadingDeposits] = useState(false)
  const [curPage, setCurPage] = useState(0)
  const [isCreateVirtualAccountModalOpen, setIsCreateVirtualAccountModalOpen] = useState(false)
  const [isVirtualAccountDetailsModalOpen, setIsVirtualAccountDetailsModalOpen] = useState(false)
  const [virtualAccounts, setVirtualAccounts] = useState<DepositAccount[]>([])
  const [deposits, setDeposits] = useState<Deposit[]>([])
  const [fiatAccountTypes, setFiatAccountTypes] = useState<FiatAccountType[]>([
    FiatAccountType.BRL,
    FiatAccountType.EUR,
    FiatAccountType.MXN,
    FiatAccountType.USD
  ])

  const [virtualAccountsCapabilities, setVirtualAccountsCapabilities] = useState<BridgeVirtualAccountCapabilities>({
    canOpenAch: false,
    canOpenSepa: false,
    canOpenSpei: false,
    canOpenPix: false
  })

  const [currentDetails, setCurrentDetails] =
    useState<{
      subtitle: string
      subtitleIcon: string
      items: Array<{ title: string, value: string, isCopy: boolean }>
      id: string
      status: DepositAccount['status']
    }>({ subtitle: '', subtitleIcon: '', items: [], id: '', status: 'disabled' })
  const [selectedTab, setSelectedTab] = useState<string>('Details')
  const [accountDeposits, setAccountDeposits] = useState<Deposit[]>([])
  const [isLoadingAccountDeposits, setIsLoadingAccountDeposits] = useState(false)

  const fetchAccountDeposits = async (id: string): Promise<void> => {
    if (!canReadDeposits) return
    setIsLoadingAccountDeposits(true)
    setAccountDeposits([])
    Radom.listDepositsByAccountId(id)
      .then(res => setAccountDeposits(res))
      .catch(err => {
        console.error('Failed to load account deposits', err)
        setAccountDeposits([])
      })
      .finally(() => setIsLoadingAccountDeposits(false)
      )
  }

  const availableAccountTypes = useMemo(() => {
    const arr: Array<{ type: FiatAccountType, available: boolean }> = []
    if (virtualAccountsCapabilities.canOpenPix) {
      arr.push({ type: FiatAccountType.BRL, available: true })
    } else {
      arr.push({ type: FiatAccountType.BRL, available: false })
    }
    if (virtualAccountsCapabilities.canOpenSpei) {
      arr.push({ type: FiatAccountType.MXN, available: true })
    } else {
      arr.push({ type: FiatAccountType.MXN, available: false })
    }
    if (virtualAccountsCapabilities.canOpenSepa) {
      arr.push({ type: FiatAccountType.EUR, available: true })
    } else {
      arr.push({ type: FiatAccountType.EUR, available: false })
    }
    if (virtualAccountsCapabilities.canOpenAch) {
      arr.push({ type: FiatAccountType.USD, available: true })
    } else {
      arr.push({ type: FiatAccountType.USD, available: false })
    }
    return arr
  }, [virtualAccountsCapabilities])

  const availableCurrencies = useMemo(() => {
    const arr: FiatDestinationCurrency[] = [FiatDestinationCurrency.USDC]
    if (User.organization?.features?.isUsdtEnabled) {
      arr.push(FiatDestinationCurrency.USDT)
    }
    return arr
  }, [User.organization?.features])

  const getFiatDepositSummary = (deposit: Deposit): FiatDepositSummary => {
    const paymentMethodOut = getMethod(
      deposit.depositMethod.Fiat?.onrampNetwork || '',
      deposit.depositMethod.Fiat?.onrampToken)
    const paymentMethodIn = getMethod(
      'Fiat',
      deposit.depositMethod.Fiat?.fiatCurrency)
    return {
      in: {
        img: paymentMethodIn.logo,
        amount: deposit.fiatGrossAmount || '',
        ticker: paymentMethodIn.ticker
      },
      out: {
        img: paymentMethodOut.logo,
        amount: deposit.netAmount,
        ticker: paymentMethodOut.ticker
      }
    }
  }

  const getAccountType = (acc: DepositAccount): FiatAccountType | null => acc.accountDetails.Fiat?.accountType.spei
    ? FiatAccountType.MXN
    : acc.accountDetails.Fiat?.accountType.pix
      ? FiatAccountType.BRL
      : acc.accountDetails.Fiat?.accountType.iban
        ? FiatAccountType.EUR
        : acc.accountDetails.Fiat?.accountType.us
          ? FiatAccountType.USD
          : null

  const fetchVirtualAccounts = async (): Promise<void> => {
    setIsLoading(true)
    setVirtualAccounts([])

    Radom.listDepositAccounts(testMode, 0, batch, undefined, [DepositAccountType.Fiat])
      .then(res => {
        setVirtualAccounts(res.data)
      })
      .catch(err => {
        console.error('Failed to load virtual accounts', err)
        setVirtualAccounts([])
      })
      .finally(() => setIsLoading(false)
      )
  }

  const fetchDeposits = async (): Promise<void> => {
    if (!canReadDeposits) return
    setIsLoadingDeposits(true)
    setDeposits([])

    Radom.listDeposits(testMode, [GeneralDepositMethod.Fiat], 0, batch)
      .then(async ({ data: firstData, totalCount }) => {
        let deps = [...firstData]

        if (totalCount > batch) {
          const totalBatches = Math.ceil(totalCount / batch)
          for (let i = 1; i < totalBatches; i++) {
            const offset = i * batch
            const { data: nextData } = await Radom.listDeposits(
              testMode,
              [GeneralDepositMethod.Fiat],
              offset,
              batch
            )
            deps = deps.concat(nextData)
          }
        }
        setDeposits(deps)
      })
      .catch(err => {
        console.error('Failed to load virtual account deposits', err)
        setDeposits([])
      })
      .finally(() => {
        setIsLoadingDeposits(false)
      })
  }

  const debouncedFetchVirtualAccounts = useMemo(() => _.debounce(fetchVirtualAccounts, 300), [curPage, testMode])
  const debouncedFetchDeposits = useMemo(() => _.debounce(fetchDeposits, 300), [testMode, canReadDeposits])

  const filteredVirtualAccounts = useMemo(() => virtualAccounts.filter(item => {
    if (!item.bastionId) return false
    const accType = getAccountType(item)
    if (!accType) return false
    return fiatAccountTypes.includes(accType)
  }), [virtualAccounts, fiatAccountTypes])

  const defaultCurrency = User.organization?.defaultCurrency ?? 'USD'
  const canUseLiveMode = User.organization?.verificationStatus === 'Verified'
  const activeVirtualAccountCount = filteredVirtualAccounts.filter(account => account.status === 'active').length
  const enabledAccountTypeCount = _.uniq(
    filteredVirtualAccounts.map(account => getAccountType(account)).filter(Boolean)
  ).length
  const bastionAccountIds = useMemo(
    () => new Set(filteredVirtualAccounts.map(account => account.id)),
    [filteredVirtualAccounts]
  )
  const visibleDeposits = useMemo(
    () => deposits.filter(deposit => bastionAccountIds.has(deposit.depositAccountId)),
    [deposits, bastionAccountIds]
  )
  const visibleMetricsData = useMemo(() => {
    const end = endOfDay(new Date())
    const data = Array(31)
      .fill(0)
      .map((_, idx) => ({
        x: subDays(end, idx).getTime(),
        y: 0
      }))
      .reverse()

    visibleDeposits.forEach((deposit: Deposit) => {
      const eventTime = new Date(deposit.depositedAt).getTime()
      if (eventTime < data[0].x) return

      const fiat = deposit.depositMethod.Fiat?.fiatCurrency ?? User.organization?.defaultCurrency ?? 'USD'
      const conversion = Treasury.priceQuotes.find(
        (price) => price.from.toLowerCase() === fiat.toLowerCase()
      )

      for (let i = 0; i < data.length; i++) {
        if (eventTime > data[i].x) continue
        data[i].y += Number(deposit.fiatGrossAmount ?? 0) * (Number(conversion?.value) ?? 1)
        break
      }
    })

    return data
  }, [visibleDeposits, User.organization?.defaultCurrency, Treasury.priceQuotes])
  const visibleTotalDeposit = visibleMetricsData.reduce((acc, curr) => acc + curr.y, 0)
  const destinationCurrencies = _.uniq(filteredVirtualAccounts.map(account =>
    account.accountDetails.Fiat?.accountType.iban?.destinationCurrency ||
    account.accountDetails.Fiat?.accountType.us?.destinationCurrency ||
    account.accountDetails.Fiat?.accountType.spei?.destinationCurrency ||
    account.accountDetails.Fiat?.accountType.pix?.destinationCurrency ||
    'USDC'
  ))

  useEffect(() => {
    if (testMode) {
      setIsLoading(false)
      setVirtualAccounts([])
      return
    }

    debouncedFetchVirtualAccounts()
    return () => debouncedFetchVirtualAccounts.cancel()
  }, [debouncedFetchVirtualAccounts, testMode])

  useEffect(() => {
    if (testMode || !isLoaded || !canReadDeposits) {
      setCurPage(0)
      setIsLoadingDeposits(false)
      setDeposits([])
      return
    }

    setCurPage(0)
    debouncedFetchDeposits()
    return () => debouncedFetchDeposits.cancel()
  }, [debouncedFetchDeposits, testMode, isLoaded, canReadDeposits])

  useEffect(() => {
    if (testMode || !VIRTUAL_ACCOUNT_CREATION_ENABLED) {
      return
    }

    Radom.getVirtualAccountsCapabilities().then(res => setVirtualAccountsCapabilities(res)).catch(() => {})
  }, [testMode])

  return <Container>
    <DashboardPage>
      <PageHeaderRow>
        <PageHeader
          title='Virtual accounts' />
        {!testMode && VIRTUAL_ACCOUNT_CREATION_ENABLED && <PageActionRow>
          <Gate permission='WriteDepositAccount'>
            <PrimaryButton onClick={() => {
              setIsCreateVirtualAccountModalOpen(true)
            }}>
              <ButtonLeadingIcon>+</ButtonLeadingIcon>
              <span>Create account</span>
            </PrimaryButton>
          </Gate>
        </PageActionRow>}
      </PageHeaderRow>

      {testMode
        ? <Surface>
          <EmptyState style={{ flexDirection: 'column', gap: 12 }}>
            <div style={{ maxWidth: 520 }}>
              Virtual accounts are not available in test mode.
              Switch to live mode to view fiat deposit accounts.
            </div>
            <PrimaryButton
              disabled={!canUseLiveMode}
              onClick={() => {
                if (canUseLiveMode) {
                  setTestMode(false)
                }
              }}
              style={{ minHeight: 40 }}>
              <span>{canUseLiveMode ? 'Switch to live mode' : 'Verify organization to use live mode'}</span>
            </PrimaryButton>
          </EmptyState>
        </Surface>
        : <>
          {pooledEurInstructions?.available && <Surface>
            <SurfaceHeader>
              <div>
                <SurfaceTitle>EUR bank transfer (SEPA)</SurfaceTitle>
              </div>
              <InlinePill $tone='green'>Pooled account</InlinePill>
            </SurfaceHeader>
            <div style={{ padding: '4px 20px 16px 20px', display: 'flex', flexDirection: 'column', gap: 4 }}>
              <div style={{ fontSize: 13, color: RADOM_COLORS.GRAY_DARKER, paddingBottom: 8 }}>
                Send EUR to the account below. Your payment reference must be included on every
                transfer — it is how your deposit is credited to this organization. Deposits are
                reconciled and credited in USDC after review.
              </div>
              <PooledInstructionRow>
                <span style={{ color: RADOM_COLORS.GRAY_DARKER }}>Payment reference (required)</span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600 }}>
                  {pooledEurInstructions.reference}
                  <CopyButton text={pooledEurInstructions.reference ?? ''} />
                </span>
              </PooledInstructionRow>
              <PooledInstructionRow>
                <span style={{ color: RADOM_COLORS.GRAY_DARKER }}>IBAN</span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  {pooledEurInstructions.iban}
                  <CopyButton text={pooledEurInstructions.iban ?? ''} />
                </span>
              </PooledInstructionRow>
              {pooledEurInstructions.bic && <PooledInstructionRow>
                <span style={{ color: RADOM_COLORS.GRAY_DARKER }}>BIC</span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  {pooledEurInstructions.bic}
                  <CopyButton text={pooledEurInstructions.bic} />
                </span>
              </PooledInstructionRow>}
              <PooledInstructionRow>
                <span style={{ color: RADOM_COLORS.GRAY_DARKER }}>Beneficiary</span>
                <span>{pooledEurInstructions.beneficiary}</span>
              </PooledInstructionRow>
              {pooledEurInstructions.bankName && <PooledInstructionRow>
                <span style={{ color: RADOM_COLORS.GRAY_DARKER }}>Bank</span>
                <span>{pooledEurInstructions.bankName}</span>
              </PooledInstructionRow>}
            </div>
          </Surface>}
          <Surface>
            <SurfaceHeader>
              <div>
                <SurfaceTitle>Deposit accounts</SurfaceTitle>
              </div>
              <InlinePill $tone='green'>{activeVirtualAccountCount} active</InlinePill>
            </SurfaceHeader>
            <SummaryStrip>
              <SummaryStripItem>
                <SummaryLabel>30-day volume</SummaryLabel>
                <SummaryValue>
                  {!canReadDeposits
                    ? '—'
                    : isLoadingDeposits
                      ? <LoadingBar style={{ height: 28, width: 130 }} />
                      : formatCurrency(visibleTotalDeposit, defaultCurrency)}
                </SummaryValue>
                <SummaryMeta>
                  {canReadDeposits ? `Estimated in ${defaultCurrency}` : 'Requires the Read deposits permission'}
                </SummaryMeta>
              </SummaryStripItem>
              <SummaryStripItem>
                <SummaryLabel>Virtual accounts</SummaryLabel>
                <SummaryValue>
                  {isLoading ? <LoadingBar style={{ height: 28, width: 64 }} /> : filteredVirtualAccounts.length}
                </SummaryValue>
                <SummaryMeta>{activeVirtualAccountCount} active accounts</SummaryMeta>
              </SummaryStripItem>
              <SummaryStripItem>
                <SummaryLabel>Available account types</SummaryLabel>
                <SummaryValue>{enabledAccountTypeCount}</SummaryValue>
                <SummaryMeta>Supported fiat deposit rails</SummaryMeta>
              </SummaryStripItem>
              <SummaryStripItem>
                <SummaryLabel>Settlement currencies</SummaryLabel>
                <SummaryValue style={{ fontSize: 18 }}>
                  {destinationCurrencies.length > 0 ? destinationCurrencies.join(', ') : 'USDC'}
                </SummaryValue>
                <SummaryMeta>Settlement currencies configured</SummaryMeta>
              </SummaryStripItem>
            </SummaryStrip>
          </Surface>

          <DetailGrid>
            <Surface>
              <SurfaceHeader>
                <div>
                  <SurfaceTitle>Accounts</SurfaceTitle>
                </div>
                <FilterBar>
                  <Dropdown
                    selectedContent={<div style={{ fontSize: 14 }}>Account type</div>}
                    dropdownContent={<div>
                      {Object.values(FiatAccountType).map(s =>
                        <DropdownItem
                          key={s}
                          style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: 14 }}
                          onClick={() => {
                            if (isLoading) return
                            setFiatAccountTypes((prev) =>
                              prev.includes(s)
                                ? prev.filter((x) => x !== s)
                                : [...prev, s])
                          }}>
                          <Checkbox size={16} checked={fiatAccountTypes.includes(s)} />
                          <img
                            style={{
                              width: 16,
                              maxHeight: 16,
                              maxWidth: 16
                            }}
                            src={`https://pay.radom.com/flags/${FIAT_ACCOUNT_FLAGS[s]}.svg`}
                          />
                          <span>{s}</span>
                        </DropdownItem>
                      )}
                    </div>} />
                </FilterBar>
              </SurfaceHeader>

              {isLoading
                ? <div style={{ minHeight: 180, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <Spinner/>
                </div>
                : virtualAccounts.length === 0
                  ? <EmptyState>No virtual accounts yet.</EmptyState>
                  : filteredVirtualAccounts.length > 0
                    ? <AccountList>
                    {filteredVirtualAccounts.map((item) => {
                      const accountName = item.name
                      const accountType = getAccountType(item)
                      const sourceCurrency = item.accountDetails.Fiat?.accountType.iban?.sourceCurrency ||
              item.accountDetails.Fiat?.accountType.us?.sourceCurrency ||
              item.accountDetails.Fiat?.accountType.spei?.sourceCurrency ||
              item.accountDetails.Fiat?.accountType.pix?.sourceCurrency
                      const destinationCurrency = item.accountDetails.Fiat?.accountType.iban?.destinationCurrency ||
              item.accountDetails.Fiat?.accountType.us?.destinationCurrency ||
              item.accountDetails.Fiat?.accountType.spei?.destinationCurrency ||
              item.accountDetails.Fiat?.accountType.pix?.destinationCurrency
                      const inPaymentMethod = getMethod('Fiat', sourceCurrency?.toUpperCase() || accountType || 'USD')
                      const outPaymentMethod = getMethodLogoByTicker(destinationCurrency?.toUpperCase() ?? 'USDC')

                      const items: Array<{ title: string, value: string, isCopy: boolean }> = []

                      const bankBeneficiaryName = item.accountDetails.Fiat?.accountType.us?.bankBeneficiaryName
                      if (bankBeneficiaryName) {
                        items.push({ title: 'Beneficiary name', value: bankBeneficiaryName, isCopy: true })
                      }

                      const iban = item.accountDetails.Fiat?.accountType.iban?.iban
                      if (iban) {
                        items.push({ title: 'IBAN', value: iban, isCopy: true })
                      }

                      const bic = item.accountDetails.Fiat?.accountType.iban?.bic
                      if (bic) {
                        items.push({ title: 'BIC', value: bic, isCopy: true })
                      }

                      const routingNumber = item.accountDetails.Fiat?.accountType.us?.routingNumber
                      if (routingNumber) {
                        items.push({ title: 'Routing number', value: routingNumber, isCopy: true })
                      }

                      const accountNumber = item.accountDetails.Fiat?.accountType.us?.accountNumber
                      if (accountNumber) {
                        items.push({ title: 'Account number', value: accountNumber, isCopy: true })
                      }

                      const clabe = item.accountDetails.Fiat?.accountType.spei?.clabe
                      if (clabe) {
                        items.push({ title: 'CLABE', value: clabe, isCopy: true })
                      }

                      const brCode = item.accountDetails.Fiat?.accountType.pix?.brCode
                      if (brCode) {
                        items.push({ title: 'BR CODE', value: brCode, isCopy: true })
                      }

                      const bankName = item.accountDetails.Fiat?.bankName
                      if (bankName) {
                        items.push({ title: 'Bank name', value: bankName, isCopy: true })
                      }

                      const bankAddress = item.accountDetails.Fiat?.bankAddress
                      if (bankAddress) {
                        items.push({ title: 'Bank address', value: bankAddress, isCopy: true })
                      }

                      const paymentRailsItems = (item.accountDetails.Fiat?.accountType.iban?.sourcePaymentRails ||
              item.accountDetails.Fiat?.accountType.us?.sourcePaymentRails ||
              item.accountDetails.Fiat?.accountType.spei?.sourcePaymentRails ||
              item.accountDetails.Fiat?.accountType.pix?.sourcePaymentRails)?.map(str => {
                        const parts = str.split('_')
                        const cleaned = parts.length === 2 ? parts[0] : str
                        // Bastion US virtual accounts settle over Fedwire even if the
                        // stored rail says "ach".
                        if (item.bastionId && cleaned.toLowerCase() === 'ach') {
                          return 'FEDWIRE'
                        }
                        return cleaned.charAt(0).toUpperCase() + cleaned.slice(1).toLowerCase()
                      })

                      if (paymentRailsItems) {
                        items.push({ title: 'Payment rails', value: paymentRailsItems.join(', '), isCopy: false })
                      }

                      const destinationPaymentRail =
              item.accountDetails.Fiat?.accountType.iban?.destinationPaymentRail ||
              item.accountDetails.Fiat?.accountType.us?.destinationPaymentRail ||
              item.accountDetails.Fiat?.accountType.spei?.destinationPaymentRail ||
              item.accountDetails.Fiat?.accountType.pix?.destinationPaymentRail

                      items.push({ title: 'Destination currency', value: destinationCurrency?.toUpperCase() ?? 'USDC', isCopy: false })

                      items.push({ title: 'Destination network', value: destinationRailNetwork(destinationPaymentRail), isCopy: false })

                      return <AccountCard key={item.id} type='button' onClick={() => {
                        setCurrentDetails({
                          subtitle: accountName,
                          subtitleIcon: `https://pay.radom.com/flags/${FIAT_ACCOUNT_FLAGS[accountType || '']}.svg`,
                          items,
                          id: item.id,
                          status: item.status
                        })
                        setSelectedTab('Details')
                        setIsVirtualAccountDetailsModalOpen(true)
                        if (accountDeposits.length === 0 || accountDeposits[0].depositAccountId !== item.id) {
                          fetchAccountDeposits(item.id)
                        }
                      }}>
                        <AccountIdentity>
                          <img
                            style={{
                              width: 28,
                              height: 28
                            }}
                            src={`https://pay.radom.com/flags/${FIAT_ACCOUNT_FLAGS[accountType || '']}.svg`}
                          />
                          <AccountText>
                            <AccountTitle>{accountName}</AccountTitle>
                            <AccountMeta>{cutString(item.id, 12)} · {item.status}</AccountMeta>
                          </AccountText>
                        </AccountIdentity>
                        <AccountFlow>
                          {inPaymentMethod.logo && <img src={inPaymentMethod.logo} style={{ width: 24, height: 24 }} />}
                          <span style={{ fontSize: 12 }}>{inPaymentMethod.ticker}</span>
                          <Chevron style={{ opacity: 0.65, transform: 'rotate(-90deg)' }} />
                          <span style={{ fontSize: 12 }}>{destinationCurrency?.toUpperCase() ?? 'USDC'}</span>
                          {outPaymentMethod && <img src={outPaymentMethod} style={{ width: 24, height: 24 }} />}
                        </AccountFlow>
                      </AccountCard>
                    })}
                    </AccountList>
                    : <EmptyState>No virtual accounts match the selected filters.</EmptyState>}
            </Surface>

            <Surface>
              <SurfaceHeader>
                <div>
                  <SurfaceTitle>Recent deposits</SurfaceTitle>
                </div>
              </SurfaceHeader>
              {!canReadDeposits &&
              <EmptyState>You don&apos;t have permission to access deposits</EmptyState>}
              {canReadDeposits && visibleMetricsData.filter(item => item.y !== 0).length > 0 &&
              <DashboardAreaChart
                accentColor='#3464e6'
                showYAxis
                isLoading={isLoadingDeposits}
                height={220}
                data={visibleMetricsData}
                formatXAxisLabelFn={val => {
                  return format(new Date(val), 'MMM d')
                }}
                formatXLabelFn={val => {
                  return format(new Date(val), 'MMM d, yyyy')
                }}
                formatYLabelFn={(v: number) => formatCurrency(v, defaultCurrency)}
              />
              }
              {canReadDeposits &&
              <DepositTableShell>
                <Table
                  borderless
                  disableOnClickState
                  onClickRow={i => {
                    navigate(`/deposit/${visibleDeposits[i].id}?source=virtual_accounts`)
                  }}
                  headers={['Account', 'Summary', 'Date'].map(h => <span key={h}>{h}</span>)}
                  isLoading={isLoadingDeposits}
                  items={visibleDeposits.map(d => {
                    const summary = getFiatDepositSummary(d)
                    const acc = virtualAccounts.find(acc => acc.id === d.depositAccountId)
                    const name = d.depositAccountName || acc?.name || d.depositAccountId
                    let icon: string | null = null
                    if (acc) {
                      const accountType = getAccountType(acc)
                      icon = `https://pay.radom.com/flags/${FIAT_ACCOUNT_FLAGS[accountType || '']}.svg`
                    }

                    return [
                      <div key={'1'} style={{ display: 'flex', gap: 8, alignItems: 'center', minWidth: 0 }}>
                        {icon && <img src={icon} style={{ width: 16, height: 16 }} />}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                          <span style={{ fontSize: 12.5 }}>{cutString(name, 16)}</span>
                          <span style={{ color: '#7d8492', fontSize: 11.5 }}>{cutString(d.depositAccountId, 8)}</span>
                        </div>
                      </div>,
                      <div key={'2'} style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                          <img src={summary.in.img} style={{ width: 16, height: 16 }} />
                          <span style={{ fontSize: 12.5, color: '#20242d' }}>
                        Received {roundAssetAmount(summary.in.ticker, Number(summary.in.amount))} {summary.in.ticker}
                          </span>
                        </div>
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', color: '#7d8492' }}>
                          <Chevron style={{ opacity: 0.65, transform: 'rotate(-90deg)' }} />
                          <img src={summary.out.img} style={{ width: 16, height: 16 }} />
                          <span style={{ fontSize: 12.5 }}>
                        Settled {roundAssetAmount(summary.out.ticker, Number(summary.out.amount))} {summary.out.ticker}
                          </span>
                        </div>
                      </div>,
                      <div key={'3'}>
                        <TimeText date={new Date(d.depositedAt)} fontSize={12} />
                      </div>
                    ]
                  })}
                  emptyPlaceholderText='No deposits yet'
                />
              </DepositTableShell>}
            </Surface>
          </DetailGrid>
        </>}

      {
        VIRTUAL_ACCOUNT_CREATION_ENABLED && isCreateVirtualAccountModalOpen &&
        <CreateVirtualAccountModal
          title='Create virtual account'
          visible={isCreateVirtualAccountModalOpen}
          availableAccountTypes={availableAccountTypes}
          availableCurrencies={availableCurrencies}
          onClose={() => setIsCreateVirtualAccountModalOpen(false)}
          onAccountCreated={() => {
            setIsCreateVirtualAccountModalOpen(false)
            debouncedFetchVirtualAccounts()
          }} />
      }

      <AccountDetailsDrawer
        visible={isVirtualAccountDetailsModalOpen}
        onClose={() => setIsVirtualAccountDetailsModalOpen(false)}
        eyebrow='Virtual account'
        title={currentDetails.subtitle}
        tabs={canReadDeposits ? ['Details', 'Deposits'] : ['Details']}
        selectedTab={selectedTab}
        onTabChange={setSelectedTab}
        menu={VIRTUAL_ACCOUNT_CREATION_ENABLED && currentDetails.status === 'active'
          ? <Gate permission='WriteDepositAccount'>
            <ConfirmButtonModal
              title='Delete account'
              description='Are you sure you want to delete this account?'
              action='Delete account'
              onConfirm={async () => {
                setIsVirtualAccountDetailsModalOpen(false)
                setIsLoading(true)
                Radom.deleteDepositAccount(currentDetails.id)
                  .then(async () => await fetchVirtualAccounts())
                  .then(() => {
                    successToast('Account deleted')
                  })
              }}
              ActionComponent={(props: any) => <DropdownItem {...props}>Delete account</DropdownItem>}
            />
          </Gate>
          : undefined}
      >
        {selectedTab === 'Details'
          ? <>
            {currentDetails.items.map((item, idx) =>
              <DrawerField
                key={idx}
                label={item.title}
                value={item.value}
                copy={currentDetails.status === 'active' && item.isCopy
                  ? item.value
                  : undefined}
              />
            )}
          </>
          : <Table
            borderless
            disableOnClickState
            headers={['Deposit ID', 'Summary', 'Date'].map(h => <span key={h}>{h}</span>)}
            isLoading={isLoadingAccountDeposits}
            items={accountDeposits.map(d => {
              const summary = getFiatDepositSummary(d)
              return [
                <div key={'1'} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <ExternalLinkText
                    onClick={e => e.stopPropagation()}
                    target="_blank"
                    href={`/deposit/${d.id}?source=virtual_accounts`}>
                    {shortAddress(d.id, 3)}
                    <LinkExternal stroke={RADOM_COLORS.BLACK} width={14} />
                  </ExternalLinkText>
                  <CopyButton text={d.id} />
                </div>,
                <div key={'2'} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <img src={summary.in.img} style={{ width: 20, height: 20 }} />
                  <span>{roundAssetAmount(summary.in.ticker, Number(summary.in.amount))}</span>
                  <span>{summary.in.ticker}</span>
                  <Chevron style={{ opacity: 0.65, transform: 'rotate(-90deg)' }} />
                  <img src={summary.out.img} style={{ width: 20, height: 20 }} />
                  <span>{roundAssetAmount(summary.out.ticker, Number(summary.out.amount))}</span>
                  <span>{summary.out.ticker}</span>
                </div>,
                <div key={'3'}>
                  <TimeText date={new Date(d.depositedAt)} />
                </div>
              ]
            })}
            emptyPlaceholderText='No deposits yet'
          />}
      </AccountDetailsDrawer>
    </DashboardPage>
  </Container>
})

export default VirtualAccounts
